/**
 * One programme in the portfolio: what is in force, and its curriculum history.
 *
 * @module components/portfolio/PortfolioProgrammeDetail
 */

import React from "react";

import { recordedCredits } from "@/components/shared/credits";
import { isRetired } from "@/components/shared/programme-retirement";
import { curriculumRevisionLabel } from "@/components/shared/revision-labels";
import { lifecycleTone } from "@/components/shared/status-tones";
import { type AsyncState, Badge, Button, Heading, KeyValue, MutedText } from "@/components/ui";
import type { PortfolioProgrammeRevision, ProgrammeGovernanceSummary } from "@/platform";
import { formatCalendarDate } from "@/utils/calendar-dates";

import { CurriculumHistory } from "./CurriculumHistory";
import { type ProgrammeGroup, uniqueApprovedRevisions } from "./portfolio-groups";
import { anchorScope, manageableAnchor } from "./programme-anchor";
import {
  LIFECYCLE_LABEL,
  publicationReason,
  publicationState,
  TONE_BADGE,
} from "./revision-presentation";

export interface PortfolioProgrammeDetailProps {
  group: ProgrammeGroup;
  canManage: boolean;
  canPublish: boolean;
  busy?: boolean;
  historyState: (programmeId: string) => AsyncState<ProgrammeGovernanceSummary | null>;
  onRetryHistory: (programmeId: string) => void;
  onManage: (revision: PortfolioProgrammeRevision) => void;
  onCompare: (programmeId: string, revisionId: string) => void;
  onChangeCaseOpen: (programmeId: string, changeCaseId: string) => void;
}

/**
 * @example
 * ```tsx
 * <PortfolioProgrammeDetail group={group} canManage canPublish={false} … />
 * ```
 */
export function PortfolioProgrammeDetail({
  group,
  canManage,
  canPublish,
  busy = false,
  historyState,
  onRetryHistory,
  onManage,
  onCompare,
  onChangeCaseOpen,
}: PortfolioProgrammeDetailProps): React.ReactElement {
  const { primary } = group;
  const scope = anchorScope(group);
  const publication = publicationState(scope);
  const reason = publicationReason(scope);
  const manageable = manageableAnchor(group, { canManage, canPublish });
  return (
    <div className="space-y-4" data-testid="portfolio-detail">
      <div data-testid="portfolio-detail-head">
        <div className="flex flex-wrap items-start justify-between gap-2">
          <Heading level={2} size="md">
            {primary.title}
          </Heading>
          {manageable && (
            <Button
              size="sm"
              variant="outline"
              icon="gear-six"
              disabled={busy}
              disabledReason="Wait for the publication change to finish"
              onClick={() => {
                onManage(manageable);
              }}
              data-testid={`portfolio-publication-action-${manageable.curriculumRevisionId}`}
            >
              Manage
            </Button>
          )}
        </div>
        <MutedText as="p" size="caption">
          {primary.programmeCode ?? primary.awardType} · {primary.institutionalLabel} ·{" "}
          {curriculumRevisionLabel(primary.revisionNumber)} of{" "}
          {uniqueApprovedRevisions(group.revisions).length}
        </MutedText>
        <div className="mt-2 flex flex-wrap items-center gap-2">
          <Badge tone={TONE_BADGE[lifecycleTone(scope.lifecycleStatus)]} size="sm" variant="soft">
            {LIFECYCLE_LABEL[scope.lifecycleStatus] ?? scope.lifecycleStatus}
          </Badge>
          <Badge tone={publication.tone} size="sm" variant="soft">
            {publication.label}
          </Badge>
        </div>
        {reason && (
          <MutedText
            as="p"
            size="caption"
            className="mt-1"
            data-testid="portfolio-publication-reason"
          >
            {reason}
          </MutedText>
        )}
      </div>
      {/* Only the facts this programme has, so the pane is not mostly labels
          over blanks. */}
      <KeyValue
        layout="inline"
        entries={[
          { label: "Award", value: primary.awardType },
          { label: "NFQ", value: primary.nfqLevel },
          { label: "Credits", value: recordedCredits(primary.totalCredits) },
          { label: "Effective from", value: formatCalendarDate(primary.effectiveFrom) },
          {
            label: primary.endOfLife && isRetired(primary) ? "Retired" : "End of life",
            value: primary.endOfLife ? formatCalendarDate(primary.endOfLife) : undefined,
          },
        ].filter((entry) => entry.value !== null && entry.value !== undefined)}
      />
      <CurriculumHistory
        programmeId={group.programmeId}
        revisions={group.revisions}
        state={historyState(group.programmeId)}
        canManage={canManage}
        canPublish={canPublish}
        busy={busy}
        onManage={onManage}
        onCompare={onCompare}
        onChangeCaseOpen={onChangeCaseOpen}
        onRetry={onRetryHistory}
      />
    </div>
  );
}
